import { A11yModule } from '@angular/cdk/a11y';
import { BidiModule } from '@angular/cdk/bidi';
import { ObserversModule } from '@angular/cdk/observers';
import { OverlayModule } from '@angular/cdk/overlay';
import { PlatformModule } from '@angular/cdk/platform';
import { PortalModule } from '@angular/cdk/portal';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { CdkStepperModule } from '@angular/cdk/stepper';
import { CdkTableModule } from '@angular/cdk/table';
import { CommonModule } from '@angular/common';
import { Injectable, NgModule } from '@angular/core';
import { MatBottomSheetModule } from '@angular/material/bottom-sheet';
import { MatButtonToggleModule } from '@angular/material/button-toggle';
import { MatChipsModule } from '@angular/material/chips';
import { DateAdapter, MatNativeDateModule, MatRippleModule, NativeDateAdapter } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDialogModule as MatDialogModuleNew } from '@angular/material/dialog';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatLegacyAutocompleteModule as MatAutocompleteModule } from '@angular/material/legacy-autocomplete';
import { MatLegacyButtonModule as MatButtonModule } from '@angular/material/legacy-button';
import { MatLegacyCardModule as MatCardModule } from '@angular/material/legacy-card';
import { MatLegacyCheckboxModule as MatCheckboxModule } from '@angular/material/legacy-checkbox';
import { MatLegacyDialogModule as MatDialogModule } from '@angular/material/legacy-dialog';
import { MatLegacyFormFieldModule as MatFormFieldModule } from '@angular/material/legacy-form-field';
import { MatLegacyInputModule as MatInputModule } from '@angular/material/legacy-input';
import { MatLegacyListModule as MatListModule } from '@angular/material/legacy-list';
import { MatLegacyMenuModule as MatMenuModule } from '@angular/material/legacy-menu';
import {
  MatLegacyPaginatorIntl as MatPaginatorIntl,
  MatLegacyPaginatorModule as MatPaginatorModule,
} from '@angular/material/legacy-paginator';
import { MatLegacyProgressBarModule as MatProgressBarModule } from '@angular/material/legacy-progress-bar';
import { MatLegacyProgressSpinnerModule as MatProgressSpinnerModule } from '@angular/material/legacy-progress-spinner';
import { MatLegacyRadioModule as MatRadioModule } from '@angular/material/legacy-radio';
import { MatLegacySelectModule as MatSelectModule } from '@angular/material/legacy-select';
import { MatLegacySlideToggleModule as MatSlideToggleModule } from '@angular/material/legacy-slide-toggle';
import { MatLegacySliderModule as MatSliderModule } from '@angular/material/legacy-slider';
import { MatLegacySnackBarModule as MatSnackBarModule } from '@angular/material/legacy-snack-bar';
import { MatLegacyTabsModule as MatTabsModule } from '@angular/material/legacy-tabs';
import { MatLegacyTooltipModule as MatTooltipModule } from '@angular/material/legacy-tooltip';
import { MatSnackBarModule as MatSnackBarModuleNew } from '@angular/material/snack-bar';
import { MatStepperModule } from '@angular/material/stepper';
import { MatToolbarModule } from '@angular/material/toolbar';
import { TranslateService } from '@ngx-translate/core';

const modules = [
  MatButtonModule,
  MatCheckboxModule,
  MatSelectModule,
  MatFormFieldModule,
  MatInputModule,
  MatSliderModule,
  MatAutocompleteModule,
  MatDatepickerModule,
  MatNativeDateModule,
  MatPaginatorModule,
  MatRippleModule,
  MatTabsModule,
  MatRadioModule,
  MatCardModule,
  MatChipsModule,
  MatExpansionModule,
  MatButtonToggleModule,
  MatProgressBarModule,
  MatSnackBarModule,
  MatSnackBarModuleNew,
  MatMenuModule,
  MatToolbarModule,
  MatDividerModule,
  MatStepperModule,
  MatIconModule,
  MatListModule,
  MatDialogModule,
  MatDialogModuleNew,
  MatSlideToggleModule,
  MatProgressSpinnerModule,
  MatTooltipModule,
  MatBottomSheetModule,
  // CDK
  A11yModule,
  BidiModule,
  ObserversModule,
  OverlayModule,
  PlatformModule,
  PortalModule,
  ScrollingModule,
  CdkStepperModule,
  CdkTableModule,
];

@Injectable()
class CustomDateAdapter extends NativeDateAdapter {
  /**
   * Overrides the default format function for the angular material datepicker to
   * display arabic dates in the correct format.
   * @param date - Date object
   * @param displayFormat - The required format of the date. Used for non AR languages
   */
  public format(date: Date, displayFormat: any): string {
    if (this.locale.indexOf('ar') !== -1) {
      if (displayFormat.year === 'numeric' && displayFormat.month === 'short') {
        return this.getMonthNames('short')[date.getMonth()].toUpperCase() + ' ' + this.getYearName(date).toString();
      } else {
        return date.toLocaleDateString('en-gb', displayFormat);
      }
    } else {
      return date.toLocaleDateString(this.locale, displayFormat);
    }
  }

  public getYearName(date: Date): string {
    if (this.locale.indexOf('ar') !== -1) {
      return date.getFullYear().toString();
    } else {
      return super.getYearName(date);
    }
  }

  public getDateNames(): string[] {
    if (this.locale.indexOf('ar') !== -1) {
      return Array(31)
        .fill(0)
        .map((_, i) => String(i + 1));
    } else {
      return super.getDateNames();
    }
  }

  public getDayOfWeekNames(style: 'long' | 'short' | 'narrow'): string[] {
    if (this.locale.indexOf('ar') !== -1) {
      return ['الأحد', 'الأثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعه', 'السبت'];
    } else {
      return super.getDayOfWeekNames(style);
    }
  }

  public getMonthNames(style: 'long' | 'short' | 'narrow'): string[] {
    if (this.locale.indexOf('ar') !== -1) {
      return [
        'يناير',
        'فبراير',
        'مارس',
        'إبريل',
        'مايو',
        'يونيو',
        'يوليو',
        'أغسطس',
        'سبتمبر',
        'أكتوبر',
        'نوفمبر',
        'ديسمبر',
      ];
    } else {
      return super.getMonthNames(style);
    }
  }
}

// Provides different translated text for the paginator depending on language
class MatPaginatorIntlCro extends MatPaginatorIntl {
  constructor(translateService: TranslateService) {
    super();
    this.itemsPerPageLabel = '';
    this.nextPageLabel = translateService.instant('PaginationComponent:NextPage');
    this.previousPageLabel = translateService.instant('PaginationComponent:PreviousPage');
  }

  public getRangeLabel = (page: number, pageSize: number, length: number): string => {
    if (length === 0 || pageSize === 0) {
      return `0 / ${length}`;
    }

    length = Math.max(length, 0);
    const startIndex = page * pageSize;
    // If the start index exceeds the list length, do not try and fix the end index to the end.
    const endIndex = startIndex < length ? Math.min(startIndex + pageSize, length) : startIndex + pageSize;
    return `${startIndex + 1} - ${endIndex} / ${length}`;
  };
}

@NgModule({
  imports: [CommonModule, ...modules],
  exports: [...modules],
  providers: [
    { provide: DateAdapter, useClass: CustomDateAdapter },
    {
      provide: MatPaginatorIntl,
      deps: [TranslateService],
      useFactory: (translateService: TranslateService) => new MatPaginatorIntlCro(translateService),
    },
  ],
})
export class MaterialManifestModule {}
